/**
 * Determines the route a user should be redirected to when selecting a facility, based on their role and enabled modules.
 *
 * Decision logic:
 *
 * 1. If the user's role is null/undefined:
 *    - Redirects to the facility dashboard (route: 'facility_dashboard').
 *    - Rationale: safest default; could be changed to error if stricter handling is needed.
 *
 * 2. If the user's role is 'planner':
 *    - If the Planning module is enabled:
 *        - Redirects to the planning page (route: 'planning').
 *    - If the Planning module is disabled:
 *        - Returns an error: 'PLANNING_MODULE_DISABLED_FOR_PLANNER_ROLE'.
 *    - Note: Planners cannot access the dashboard or Live OEE.
 *
 * 3. If the user's role is 'admin' or 'manager':
 *    - If Planning is enabled and Live OEE is disabled:
 *        - Redirects to the planning page (route: 'planning').
 *    - If Live OEE is enabled (regardless of Planning):
 *        - Redirects to the facility dashboard (route: 'facility_dashboard').
 *    - If both modules are disabled:
 *        - Redirects to the facility dashboard (legacy behavior; could be changed to error if needed).
 *
 * 4. For all other roles (e.g., operator, viewer, etc.):
 *    - If Live OEE is enabled:
 *        - Redirects to the facility dashboard (route: 'facility_dashboard').
 *    - If Live OEE is disabled:
 *        - Redirects to no modules page (route: 'no_modules_enabled') instructing user to contact support.
 *    - Planning is considered inaccessible for these roles.
 *
 * @param role - The user's role (e.g., 'planner', 'admin', 'manager', etc.)
 * @param isLiveOEEEnabled - Whether the Live OEE module is enabled
 * @param isPlanningEnabled - Whether the Planning module is enabled
 * @param facilityId - The ID of the selected facility
 * @returns NavigationResult containing the target route (name, params) or an error message
 */
export function determineFacilityRoute(role, isLiveOEEEnabled, isPlanningEnabled, facilityId) {
    // If we can't determine the role, default to facility dashboard (safest default for now, or could error)
    if (!role) {
        return {
            name: 'facility_dashboard',
            params: { facilityId }
        };
    }
    // Constraint 1: Planner Role
    if (role === 'planner') {
        if (isPlanningEnabled) {
            return {
                name: 'planning',
                params: { facilityId }
            };
        }
        else {
            return {
                error: 'PLANNING_MODULE_DISABLED_FOR_PLANNER_ROLE'
            };
        }
    }
    // Constraint 2: Admin and Manager Roles
    if (role === 'admin' || role === 'manager') {
        // If ONLY planning is enabled (and Live OEE is disabled) -> Planning
        if (isPlanningEnabled && !isLiveOEEEnabled) {
            return {
                name: 'planning',
                params: { facilityId }
            };
        }
        // If Live OEE is enabled (alone or with Planning) -> Facility Dashboard
        // Fallback for when both are disabled -> Facility Dashboard (or handle as error if needed, but keeping legacy behavior)
        return {
            name: 'facility_dashboard',
            params: { facilityId }
        };
    }
    // Constraint 3: All other roles (Operator, Viewer, etc.)
    // Redirect to facility_dashboard (Planning inaccessible)
    return {
        name: 'facility_dashboard',
        params: { facilityId }
    };
}
